import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated! You can now sign in.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may be invalid or expired.");
    }
    setLoading(false);
  };

  return (
    <Layout hideAd>
      <div className="max-w-md mx-auto px-4 py-16" data-testid="reset-password-page">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center text-white font-black text-xl font-display mx-auto mb-3">M</div>
          <h1 className="font-display font-bold text-2xl">Choose a new password</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter and confirm your new password below.</p>
        </div>

        <div className="p-6 rounded-2xl border border-border/60 bg-card">
          {!token ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <p className="text-sm text-muted-foreground">This reset link is invalid or incomplete. Please request a new one.</p>
              <Link to="/forgot-password"><Button variant="outline" className="rounded-full mt-4">Request new link</Button></Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h2 className="font-semibold text-lg">Password updated</h2>
              <p className="text-sm text-muted-foreground mt-1">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-pw">New password</Label>
                <PasswordInput id="new-pw" data-testid="new-password-input" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="confirm-pw">Confirm new password</Label>
                <PasswordInput id="confirm-pw" data-testid="confirm-password-input" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full" size="lg" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
